// कथा क्रम: put a story's lines back in order. Tap a line (or press Enter / Space on it) to place it next; placed
// lines move with the arrow buttons (or Alt + ↑ / ↓) and go back with ✕. After checking, each placed line shows
// whether it is in the right place, and the story is shown in its real order. Every line can be heard: a
// teacher's recording when there is one, else the device voice (a separate button, never inside the line button).

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { AnswerResponse, OrderLine } from '@/prakrit/components/practice/practice-types'

import { useEffect, useRef } from 'react'
import {
    ArrowDown,
    ArrowUp,
    Check,
    Plus,
    Volume2,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { audioKey, useAudio } from '@/prakrit/lib/client/audio'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'

interface OrderQuestionProps {
    story: { pra: string, hi: string, en: string }
    lines: OrderLine[]
    showMeaning: boolean
    value: string[]
    onChange: (order: string[]) => void
    locked: boolean
    result: AnswerResponse | null
}

// 44 px tap targets; on a narrow phone the move buttons wrap under the line instead of squeezing it.
const ARROW = 'flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:border-saffron/60 hover:text-foreground disabled:pointer-events-none disabled:opacity-35'

/** Plays one line (recording, else the device voice). Renders nothing when neither can play it. */
const LinePlay: FC<{ text: string }> = ({ text }): ReactNode => {
    const { ready, has, play, playing, source } = useAudio()
    const key = audioKey(text)

    if (!ready || !has(key)) return null

    const voice = source(key) === 'voice'

    return (
        <button
            type='button'
            onClick={() => {
                play(key, 'normal').catch(() => undefined)
            }}
            aria-pressed={playing === key}
            aria-label={`${voice ? 'डिवाइस की आवाज़ में सुनें' : 'सुनें'} · ${voice ? 'Listen (device voice)' : 'Listen'}: ${text}`}
            title={voice ? 'डिवाइस की आवाज़ · Device voice' : 'शिक्षक की रिकॉर्डिंग · Teacher recording'}
            className={cn(ARROW, playing === key && 'border-saffron text-saffron-strong')}
        >
            <Volume2 className='size-4' aria-hidden='true' />
        </button>
    )
}

const LineText: FC<{ line: OrderLine, showMeaning: boolean, tappable: boolean, className?: string }> = ({ line, showMeaning, tappable, className }): ReactNode => (
    <span className={cn('flex min-w-0 flex-1 flex-col gap-0.5 break-words text-left', className)}>
        {line.speaker && <span lang='hi' className='hindi text-xs font-semibold text-muted-foreground'>{line.speaker}</span>}
        <span className='pra pra-md'>
            <PrakritText text={line.pra} plain={!tappable} />
        </span>
        {showMeaning && <Bi hi={line.hi} en={line.en} layout='inline' className='text-sm' enClassName='text-muted-foreground' />}
    </span>
)

export const OrderQuestion: FC<OrderQuestionProps> = ({ story, lines, showMeaning, value, onChange, locked, result }): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const focusNext = useRef<string | null>(null)
    const byId = new Map(lines.map(l => [l.id, l]))
    const placed = value.filter(id => byId.has(id))
    const pool = lines.filter(l => !placed.includes(l.id))
    const expected = result?.expectedOrder ?? null

    useEffect(() => {
        const selector = focusNext.current

        if (!selector) return

        focusNext.current = null
        rootRef.current?.querySelector<HTMLElement>(selector)?.focus()
    }, [value])

    const place = (id: string) => {
        if (locked) return

        const rest = pool.filter(l => l.id !== id)

        onChange([...placed, id])
        focusNext.current = rest[0] ? `[data-pool="${rest[0].id}"]` : `[data-placed-remove="${id}"]`
    }

    const remove = (id: string) => {
        if (locked) return

        onChange(placed.filter(x => x !== id))
        focusNext.current = `[data-pool="${id}"]`
    }

    const move = (id: string, delta: number) => {
        if (locked) return

        const index = placed.indexOf(id)
        const target = index + delta

        if (index < 0 || target < 0 || target >= placed.length) return

        const next = [...placed]

        next.splice(index, 1)
        next.splice(target, 0, id)
        onChange(next)

        // Keep focus on the moved line's button in the same direction; at the top or bottom that button is disabled,
        // so focus its other arrow instead (never let focus fall back to the page).
        const towards = delta < 0 ? (target === 0 ? 'down' : 'up') : (target === placed.length - 1 ? 'up' : 'down')

        focusNext.current = `[data-placed-${towards}="${id}"]`
    }

    const onRowKey = (event: KeyboardEvent<HTMLElement>, id: string) => {
        if (!event.altKey) return

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            move(id, -1)
        }
        else if (event.key === 'ArrowDown') {
            event.preventDefault()
            move(id, 1)
        }
    }

    return (
        <div ref={rootRef} className='flex flex-col gap-5'>
            <p className='flex flex-wrap items-baseline justify-center gap-x-2 text-center'>
                <span lang='pra' className='pra pra-lg'>{story.pra}</span>
                <Bi hi={story.hi} en={story.en} layout='inline' className='text-sm' enClassName='text-muted-foreground' />
            </p>

            <section aria-label='आपका क्रम · Your order' className='flex flex-col gap-2'>
                <h3 className='text-sm font-semibold'>
                    <Bi hi='आपका क्रम' en='Your order' layout='inline' always />
                </h3>
                {placed.length === 0 ? (
                    <p className='rounded-xl border-2 border-dashed border-border bg-surface-muted/60 px-4 py-5 text-center text-sm text-muted-foreground'>
                        <Bi hi='नीचे से पहली पंक्ति चुनिए, फिर दूसरी…' en='Pick the first line below, then the next…' />
                    </p>
                ) : (
                    <ol className='flex flex-col gap-2'>
                        {placed.map((id, i) => {
                            const line = byId.get(id) as OrderLine
                            const right = expected ? expected[i] === id : null

                            return (
                                <li
                                    key={id}
                                    onKeyDown={event => onRowKey(event, id)}
                                    className={cn(
                                        'flex flex-wrap items-center gap-2 rounded-xl border-2 bg-card px-3 py-2 shadow-sm',
                                        right === true && 'border-success bg-success-soft',
                                        right === false && 'border-danger bg-danger-soft',
                                        right === null && 'border-border'
                                    )}
                                >
                                    <span aria-hidden='true' className='flex size-7 shrink-0 items-center justify-center rounded-full bg-saffron/15 text-sm font-bold text-saffron-strong'>
                                        {right === true ? <Check className='size-4 text-success' /> : right === false ? <X className='size-4 text-danger' /> : i + 1}
                                    </span>
                                    {right !== null && (
                                        <span className='sr-only'>
                                            {right ? 'सही जगह · in the right place:' : 'ग़लत जगह · in the wrong place:'}
                                        </span>
                                    )}
                                    <LineText line={line} showMeaning={showMeaning || Boolean(result)} tappable={Boolean(result)} className='basis-40' />
                                    <LinePlay text={line.pra} />
                                    {!locked && (
                                        <span className='ml-auto flex shrink-0 items-center gap-1'>
                                            <button
                                                type='button'
                                                data-placed-up={id}
                                                onClick={() => move(id, -1)}
                                                disabled={i === 0}
                                                aria-label={`ऊपर ले जाएँ · Move up: ${line.pra}`}
                                                className={ARROW}
                                            >
                                                <ArrowUp className='size-4' aria-hidden='true' />
                                            </button>
                                            <button
                                                type='button'
                                                data-placed-down={id}
                                                onClick={() => move(id, 1)}
                                                disabled={i === placed.length - 1}
                                                aria-label={`नीचे ले जाएँ · Move down: ${line.pra}`}
                                                className={ARROW}
                                            >
                                                <ArrowDown className='size-4' aria-hidden='true' />
                                            </button>
                                            <button
                                                type='button'
                                                data-placed-remove={id}
                                                onClick={() => remove(id)}
                                                aria-label={`वापस हटाएँ · Take back: ${line.pra}`}
                                                className={ARROW}
                                            >
                                                <X className='size-4' aria-hidden='true' />
                                            </button>
                                        </span>
                                    )}
                                </li>
                            )
                        })}
                    </ol>
                )}
            </section>

            {pool.length > 0 && (
                <section aria-label='पंक्तियाँ · Lines' className='flex flex-col gap-2'>
                    <h3 className='text-sm font-semibold'>
                        <Bi hi='पंक्तियाँ — अगली पंक्ति चुनिए' en='Lines: choose the next one' layout='inline' always />
                    </h3>
                    <ul className='flex flex-col gap-2'>
                        {pool.map(line => (
                            <li key={line.id} className='flex items-center gap-2'>
                                <button
                                    type='button'
                                    data-pool={line.id}
                                    disabled={locked}
                                    onClick={() => place(line.id)}
                                    aria-label={`अगली पंक्ति रखें · Place next: ${line.pra}`}
                                    className='flex min-h-tap min-w-0 flex-1 items-center gap-3 rounded-xl border-2 border-dashed border-border bg-card px-3 py-2 text-left transition-colors hover:border-saffron/60 hover:bg-saffron/5 disabled:opacity-60'
                                >
                                    <Plus className='size-5 shrink-0 text-saffron-strong' aria-hidden='true' />
                                    <LineText line={line} showMeaning={showMeaning} tappable={false} />
                                </button>
                                <LinePlay text={line.pra} />
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {result && !result.correct && expected && (
                <aside className='animate-pop-in rounded-xl border border-dashed border-success/60 bg-success-soft/60 px-4 py-3'>
                    <p className='mb-2 text-sm font-semibold'>
                        <Bi hi='सही क्रम' en='The right order' layout='inline' always />
                    </p>
                    <ol className='flex list-decimal flex-col gap-1.5 pl-5'>
                        {expected.map(id => {
                            const line = byId.get(id)

                            return line ? (
                                <li key={id}>
                                    <span className='flex items-start gap-2'>
                                        <LineText line={line} showMeaning tappable />
                                        <LinePlay text={line.pra} />
                                    </span>
                                </li>
                            ) : null
                        })}
                    </ol>
                </aside>
            )}
        </div>
    )
}
